// The reference scan over an engine and a games store. A provider supplies
// the engine, the store and the rules reader; the scanning rules live here
// once: serial, one position at a time, resumable from what is stored,
// paused while something interactive holds the engine, never redone once
// complete.
import type { Engine, EngineUpdate } from '@chess/api-engine';
import type { Games } from '@chess/api-games';
import type { Line, MoveJudgement, ParsedGame, PositionAnalysis, Scan } from '@chess/api-model';
import type { Storage } from '@chess/api-storage';
import {
  LINES,
  PV_LENGTH,
  SCAN_FILE,
  judgeMove,
  parseScan,
  type Analysis,
  type AnalysisEvent,
  type ScanStatus,
} from './index.ts';

export interface AnalysisDeps {
  engine: Engine;
  games: Games;
  chess: { parsePgn(pgn: string): ParsedGame };
  /** The depth each position is searched to. */
  depth?: number;
  /** Positions scanned between writes of the file. */
  saveEvery?: number;
  /** How many times a search cut short by an interactive request is retried. */
  retries?: number;
  log?: { debug(msg: string, fields?: Record<string, unknown>): void; warn(msg: string, fields?: Record<string, unknown>): void };
}

const DEFAULT_DEPTH = 12;
const RETRY_DELAY_MS = 300;
/** The name of the folder a scan lives in, inside the game's folder. */
export const ANALYSIS_FOLDER = 'analysis';

function toLines(update: EngineUpdate): Line[] {
  return update.lines
    .filter((l) => l.pv.length > 0)
    .sort((a, b) => a.rank - b.rank)
    .slice(0, LINES)
    .map((l) => ({ uci: l.pv[0], score: { cp: l.score.cp, mate: l.score.mate }, pv: l.pv.slice(0, PV_LENGTH) }));
}

function clean(score: { cp?: number; mate?: number }): { cp?: number; mate?: number } {
  const out: { cp?: number; mate?: number } = {};
  if (score.cp !== undefined) out.cp = score.cp;
  if (score.mate !== undefined) out.mate = score.mate;
  return out;
}

export function createAnalysis(deps: AnalysisDeps): Analysis {
  const depth = deps.depth ?? DEFAULT_DEPTH;
  const saveEvery = deps.saveEvery ?? 4;
  const retries = deps.retries ?? 5;
  const listeners = new Set<(event: AnalysisEvent) => void>();
  const running = new Map<string, Promise<Scan>>();
  const statuses = new Map<string, ScanStatus>();
  let holds = 0;
  let releaseWaiters: Array<() => void> = [];
  // One scan at a time: a second game's scan waits for the first.
  let chain: Promise<unknown> = Promise.resolve();

  const emit = (event: AnalysisEvent): void => {
    for (const listener of listeners) listener(event);
  };

  const idle = (): Promise<void> => (holds === 0 ? Promise.resolve() : new Promise((r) => releaseWaiters.push(r)));

  const folderOf = (gameId: string): Storage => deps.games.folder(gameId, ANALYSIS_FOLDER);

  const load = async (gameId: string): Promise<Scan | undefined> => {
    const text = await folderOf(gameId).readText(SCAN_FILE);
    if (text === undefined) return undefined;
    try {
      return parseScan(text);
    } catch (error) {
      deps.log?.warn('stored scan unreadable, rescanning', { gameId, error: String(error) });
      return undefined;
    }
  };

  const save = async (scan: Scan): Promise<void> => {
    scan.updatedAt = new Date().toISOString();
    await folderOf(scan.gameId).write(SCAN_FILE, JSON.stringify(scan));
  };

  /** Searches one position, retrying when an interactive request cut the search short. */
  const search = async (fen: string): Promise<EngineUpdate> => {
    let last: EngineUpdate = { depth: 0, lines: [] };
    for (let attempt = 0; attempt <= retries; attempt++) {
      await idle();
      last = await deps.engine.evaluate(fen, { depth, multiPv: LINES });
      const reached = last.depth >= depth || last.lines.length === 0;
      if (reached) return last;
      deps.log?.debug('scan search cut short, retrying', { fen, depth: last.depth, attempt });
      await new Promise((r) => setTimeout(r, RETRY_DELAY_MS));
    }
    return last;
  };

  const judgeAll = (scan: Scan, game: ParsedGame): MoveJudgement[] => {
    const out: MoveJudgement[] = [];
    for (let i = 0; i < game.moves.length; i++) {
      const before = scan.positions.find((p) => p.ply === i);
      const after = scan.positions.find((p) => p.ply === i + 1);
      if (!before || !after) continue;
      const j = judgeMove(before, after, game.moves[i], i + 1);
      if (j) out.push(j);
    }
    return out;
  };

  const run = async (gameId: string): Promise<Scan> => {
    const record = await deps.games.get(gameId);
    if (!record) throw new Error(`no game ${gameId}`);
    const game = deps.chess.parsePgn(record.pgn);
    const info = await deps.engine.info();
    let scan = await load(gameId);
    if (scan?.complete) return scan;
    // A partial scan by another engine or depth is not worth continuing.
    if (scan && (scan.engine !== info.name || scan.depth !== depth || scan.total !== game.positions.length)) {
      deps.log?.debug('partial scan from another engine or depth discarded', { gameId });
      scan = undefined;
    }
    if (!scan) {
      scan = {
        schemaVersion: 1,
        gameId,
        engine: info.name,
        depth,
        total: game.positions.length,
        positions: [],
        moves: [],
        complete: false,
        updatedAt: '',
      };
    }
    const have = new Set(scan.positions.map((p) => p.ply));
    const status: ScanStatus = { running: true, done: have.size, total: scan.total };
    statuses.set(gameId, status);
    emit({ type: 'progress', gameId, done: status.done, total: status.total });
    let sinceSave = 0;
    try {
      for (let ply = 0; ply < game.positions.length; ply++) {
        if (have.has(ply)) continue;
        const fen = game.positions[ply];
        const update = await search(fen);
        const lines = toLines(update);
        const position: PositionAnalysis = {
          ply,
          fen,
          depth: update.depth,
          // A position with no line is over: the side to move is mated or stalemated.
          score: lines[0] ? clean(lines[0].score) : { mate: 0 },
          lines,
        };
        scan.positions.push(position);
        scan.positions.sort((a, b) => a.ply - b.ply);
        have.add(ply);
        status.done = have.size;
        emit({ type: 'progress', gameId, done: status.done, total: status.total });
        if (++sinceSave >= saveEvery) {
          await save(scan);
          sinceSave = 0;
        }
      }
      scan.moves = judgeAll(scan, game);
      scan.complete = true;
      await save(scan);
      emit({ type: 'complete', gameId, scan });
      return scan;
    } catch (error) {
      await save(scan).catch(() => undefined);
      emit({ type: 'failed', gameId, error: error instanceof Error ? error.message : String(error) });
      throw error;
    } finally {
      statuses.delete(gameId);
    }
  };

  return {
    get: load,
    scan(gameId) {
      const existing = running.get(gameId);
      if (existing) return existing;
      const promise = chain.then(() => run(gameId));
      chain = promise.catch(() => undefined);
      running.set(gameId, promise);
      promise.finally(() => running.delete(gameId)).catch(() => undefined);
      return promise;
    },
    status: (gameId) => statuses.get(gameId),
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    hold() {
      holds++;
      let released = false;
      return () => {
        if (released) return;
        released = true;
        holds--;
        if (holds === 0) {
          const waiters = releaseWaiters;
          releaseWaiters = [];
          for (const w of waiters) w();
        }
      };
    },
  };
}
